import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AdvancedMarker,
  APIProvider,
  ColorScheme,
  InfoWindow,
  Map as GoogleMap,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import HotelIcon from "@mui/icons-material/Hotel";
import SearchIcon from "@mui/icons-material/Search";
import { hasCoords, locateMissingPlaces } from "../placeLocation";
import mark from "../imgs/wandr-mark.svg";
import "./PlacesMap.css";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
// Advanced markers need a Map ID; DEMO_MAP_ID works for development.
const MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID";

const DEFAULT_VIEW = { center: { lat: -34.6037, lng: -58.3816 }, zoom: 12 };

export const CONTINENT_VIEWS = {
  Asia: { center: { lat: 30, lng: 100 }, zoom: 3 },
  "América Central": { center: { lat: 13, lng: -85 }, zoom: 5 },
  "América del Norte": { center: { lat: 42, lng: -100 }, zoom: 3 },
  "América del Sur": { center: { lat: -18, lng: -60 }, zoom: 3 },
  Europa: { center: { lat: 50, lng: 12 }, zoom: 4 },
  África: { center: { lat: 3, lng: 20 }, zoom: 3 },
  Oceanía: { center: { lat: -25, lng: 145 }, zoom: 3 },
};

// Below this zoom a 50 km search is too coarse to be useful, so Google results are paused.
const MIN_GOOGLE_ZOOM = 11;
const MAX_GOOGLE_RESULTS = 400;

// Kept to the cheaper fields on purpose: rating/userRatingCount bill at Google's top Places tier.
const GOOGLE_FIELDS = ["id", "displayName", "location", "formattedAddress", "googleMapsURI"];

const distanceMeters = (a, b) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(h));
};

/** Lodging from Google Places around what's visible, accumulated across searches. Each search is billed. */
const useGoogleLodging = (enabled) => {
  const map = useMap();
  const placesLib = useMapsLibrary("places");
  const [lodging, setLodging] = useState(() => new Map());
  const [status, setStatus] = useState("idle"); // idle | loading | error
  const requestId = useRef(0);

  const search = useCallback(async () => {
    if (!map || !placesLib || !enabled || map.getZoom() < MIN_GOOGLE_ZOOM) return;

    const bounds = map.getBounds();
    if (!bounds) return;
    const center = map.getCenter().toJSON();
    const corner = bounds.getNorthEast().toJSON();
    const radius = Math.min(50000, distanceMeters(center, corner));

    const id = ++requestId.current;
    setStatus("loading");
    try {
      const { places } = await placesLib.Place.searchNearby({
        fields: GOOGLE_FIELDS,
        locationRestriction: { center, radius },
        includedTypes: ["lodging"],
        maxResultCount: 20,
        language: "es",
      });
      if (id !== requestId.current) return;
      setLodging((prev) => {
        const next = new Map(prev);
        places.forEach((p) => next.set(p.id, p));
        // Keep the most recent results so a long session doesn't pile up markers.
        return next.size > MAX_GOOGLE_RESULTS
          ? new Map([...next].slice(-MAX_GOOGLE_RESULTS))
          : next;
      });
      setStatus("idle");
    } catch (error) {
      if (id !== requestId.current) return;
      console.error("Error buscando alojamientos en Google:", error);
      setStatus("error");
    }
  }, [map, placesLib, enabled]);

  return { lodging: [...lodging.values()], status, search };
};

const AppPin = ({ place, selected }) => (
  <div className={`map-pin map-pin--app${selected ? " is-selected" : ""}`}>
    <img src={mark} alt="" />
    {place.price ? `$ ${place.price}` : "Wandr"}
  </div>
);

const GooglePin = ({ selected }) => (
  <div className={`map-pin map-pin--google${selected ? " is-selected" : ""}`}>
    <HotelIcon />
  </div>
);

const AppInfo = ({ place, onOpen }) => (
  <div className="map-info">
    {place.imageUrls?.[0] && <img className="map-info__img" src={place.imageUrls[0]} alt={place.title} />}
    <span className="map-info__badge map-info__badge--app">Wandr</span>
    <strong className="map-info__title">{place.title}</strong>
    {place.claimStatus === "verified" && <span className="map-info__verified">✓ Alojamiento verificado</span>}
    {place.price && <span className="map-info__price">$ {place.price} por día</span>}
    <span className="map-info__meta">{[place.city, place.country].filter(Boolean).join(", ")}</span>
    <Button size="small" variant="contained" fullWidth sx={{ mt: 1 }} onClick={() => onOpen(place.id)}>
      Ver publicación
    </Button>
  </div>
);

const GoogleInfo = ({ place, onClaim }) => (
  <div className="map-info">
    <span className="map-info__badge map-info__badge--google">Google</span>
    <strong className="map-info__title">{place.displayName}</strong>
    {place.formattedAddress && <span className="map-info__meta">{place.formattedAddress}</span>}
    {place.googleMapsURI && (
      <Button
        size="small"
        variant="outlined"
        fullWidth
        sx={{ mt: 1 }}
        href={place.googleMapsURI}
        target="_blank"
        rel="noopener noreferrer"
      >
        Ver en Google Maps
      </Button>
    )}
    <button type="button" className="map-info__claim" onClick={() => onClaim(place.id)}>
      ¿Es tu alojamiento? <strong>Publicalo en Wandr</strong>
    </button>
  </div>
);

const MapContent = ({ places, continent, focus, onOpenPlace, onClaimPlace }) => {
  const map = useMap();
  const [layers, setLayers] = useState({ app: true, google: true });
  const [selected, setSelected] = useState(null); // { source: 'app' | 'google', id }
  const [located, setLocated] = useState({}); // id -> { lat, lng } found this session
  const { lodging: googleLodging, status, search } = useGoogleLodging(layers.google);
  const [zoomedOut, setZoomedOut] = useState(false);
  const [canSearchHere, setCanSearchHere] = useState(false);
  // Google is only queried on its own for the first view and after centering on the user;
  // after that the user asks with "Buscar en esta zona", so panning around costs nothing.
  const autoSearch = useRef(true);

  // Older publications have no coordinates yet; geocode them in the background.
  useEffect(() => {
    locateMissingPlaces(places, (id, coords) => setLocated((prev) => ({ ...prev, [id]: coords })));
  }, [places]);

  const appPlaces = useMemo(
    () =>
      places
        .map((p) => (hasCoords(p) ? p : located[p.id] ? { ...p, ...located[p.id] } : null))
        .filter(Boolean),
    [places, located]
  );

  // Start on the user's location when there's no continent to show.
  useEffect(() => {
    if (!map || continent || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        autoSearch.current = true;
        map.panTo({ lat: coords.latitude, lng: coords.longitude });
        map.setZoom(13);
      },
      () => {},
      { timeout: 8000 }
    );
    // Only on first load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    const view = CONTINENT_VIEWS[continent];
    if (!map || !view) return;
    map.panTo(view.center);
    map.setZoom(view.zoom);
  }, [map, continent]);

  // Search box picks: frame a Google place, or center on a Wandr listing and open it.
  // A listing without coordinates waits here until it's geocoded.
  const handledFocus = useRef(null);
  useEffect(() => {
    if (!map || !focus || handledFocus.current === focus) return;

    if (focus.placeId) {
      const place = appPlaces.find((p) => p.id === focus.placeId);
      if (!place) return;
      setLayers((prev) => ({ ...prev, app: true }));
      map.panTo({ lat: place.lat, lng: place.lng });
      map.setZoom(Math.max(map.getZoom(), 15));
      setSelected({ source: "app", id: place.id });
    } else if (focus.viewport) {
      map.fitBounds(focus.viewport);
    } else if (focus.location) {
      map.panTo(focus.location);
      map.setZoom(focus.zoom ?? 15);
    } else {
      return;
    }
    handledFocus.current = focus;
    // A deliberate jump, like centering on the user: load Google lodging there right away.
    autoSearch.current = true;
  }, [map, focus, appPlaces]);

  // A Google hotel whose owner published and got verified in Wandr is shown only as the Wandr listing.
  const lodging = useMemo(() => {
    const claimed = new Set(
      places.filter((p) => p.claimStatus === "verified" && p.googlePlaceId).map((p) => p.googlePlaceId)
    );
    return googleLodging.filter((p) => !claimed.has(p.id));
  }, [googleLodging, places]);

  const handleIdle = () => {
    const tooFar = map.getZoom() < MIN_GOOGLE_ZOOM;
    setZoomedOut(tooFar);
    if (tooFar || !layers.google) {
      setCanSearchHere(false);
    } else if (autoSearch.current) {
      autoSearch.current = false;
      setCanSearchHere(false);
      search();
    } else {
      setCanSearchHere(true);
    }
  };

  const searchHere = () => {
    setCanSearchHere(false);
    search();
  };

  const selectedApp = selected?.source === "app" && appPlaces.find((p) => p.id === selected.id);
  const selectedGoogle = selected?.source === "google" && lodging.find((p) => p.id === selected.id);
  const selectedPlace = selectedApp || selectedGoogle;
  const selectedPosition = selectedApp
    ? { lat: selectedApp.lat, lng: selectedApp.lng }
    : selectedGoogle?.location?.toJSON();

  const toggle = (layer) => {
    setLayers((prev) => ({ ...prev, [layer]: !prev[layer] }));
    // Turning Google back on offers a search instead of running one.
    if (layer === "google" && !layers.google && !zoomedOut) setCanSearchHere(true);
  };

  return (
    <>
      <GoogleMap
        mapId={MAP_ID}
        defaultCenter={DEFAULT_VIEW.center}
        defaultZoom={DEFAULT_VIEW.zoom}
        colorScheme={ColorScheme.DARK}
        gestureHandling="greedy"
        disableDefaultUI
        zoomControl
        clickableIcons={false}
        onIdle={handleIdle}
        onClick={() => setSelected(null)}
      >
        {layers.google &&
          lodging.map((p) => (
            <AdvancedMarker
              key={p.id}
              position={p.location}
              title={p.displayName}
              onClick={() => setSelected({ source: "google", id: p.id })}
            >
              <GooglePin selected={selected?.source === "google" && selected.id === p.id} />
            </AdvancedMarker>
          ))}

        {layers.app &&
          appPlaces.map((p) => (
            <AdvancedMarker
              key={p.id}
              position={{ lat: p.lat, lng: p.lng }}
              title={p.title}
              zIndex={1000}
              onClick={() => setSelected({ source: "app", id: p.id })}
            >
              <AppPin place={p} selected={selected?.source === "app" && selected.id === p.id} />
            </AdvancedMarker>
          ))}

        {selectedPlace && selectedPosition && (
          <InfoWindow
            position={selectedPosition}
            pixelOffset={[0, -38]}
            onCloseClick={() => setSelected(null)}
            headerDisabled
          >
            {selectedApp ? (
              <AppInfo place={selectedApp} onOpen={onOpenPlace} />
            ) : (
              <GoogleInfo place={selectedGoogle} onClaim={onClaimPlace} />
            )}
          </InfoWindow>
        )}
      </GoogleMap>

      <div className="map-legend">
        <button
          type="button"
          className={`map-chip map-chip--app${layers.app ? "" : " is-off"}`}
          onClick={() => toggle("app")}
        >
          <span className="map-chip__dot" /> Wandr ({appPlaces.length})
        </button>
        <button
          type="button"
          className={`map-chip map-chip--google${layers.google ? "" : " is-off"}`}
          onClick={() => toggle("google")}
        >
          <span className="map-chip__dot" /> Google ({lodging.length})
        </button>
      </div>

      {layers.google && canSearchHere && status !== "loading" && (
        <button type="button" className="map-search-here" onClick={searchHere}>
          <SearchIcon /> Buscar en esta zona
        </button>
      )}

      {layers.google && (status !== "idle" || zoomedOut) && (
        <div className="map-status">
          {status === "loading" ? (
            <>
              <CircularProgress size={12} sx={{ color: "inherit" }} /> Buscando alojamientos...
            </>
          ) : zoomedOut ? (
            "Acercá el mapa para ver alojamientos de Google"
          ) : (
            "No se pudieron cargar los alojamientos de Google"
          )}
        </div>
      )}
    </>
  );
};

const MapMessage = ({ title, children }) => (
  <div className="places-map__missing">
    <Typography variant="h6">{title}</Typography>
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  </div>
);

// Keeps a Google Maps failure from taking the whole page down with it.
class MapErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    console.error("Error en el mapa:", error);
  }

  render() {
    if (this.state.error) {
      return <MapMessage title="No se pudo cargar el mapa">Intentá recargar la página.</MapMessage>;
    }
    return this.props.children;
  }
}

// Google calls this global when the key is rejected (wrong key, domain not allowed, billing off).
// The map stays mounted underneath (unmounting its broken markers throws) and a message covers it.
const useMapsAuthFailure = () => {
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const previous = window.gm_authFailure;
    window.gm_authFailure = () => {
      setFailed(true);
      if (previous) previous();
    };
    return () => {
      window.gm_authFailure = previous;
    };
  }, []);
  return failed;
};

/** `focus`: `{ placeId }` or `{ location, viewport, zoom }`; pass a new object each time to move the map. */
const PlacesMap = ({ places, continent, focus, onOpenPlace, onClaimPlace, sx }) => {
  const authFailed = useMapsAuthFailure();

  if (!API_KEY) {
    return (
      <Box className="places-map" sx={sx}>
        <MapMessage title="Falta configurar Google Maps">
          Agregá <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> en el archivo <code>.env</code> y reiniciá la app.
        </MapMessage>
      </Box>
    );
  }

  return (
    <Box className="places-map" sx={sx}>
      <MapErrorBoundary>
        <APIProvider apiKey={API_KEY} language="es">
          <MapContent
            places={places}
            continent={continent}
            focus={focus}
            onOpenPlace={onOpenPlace}
            onClaimPlace={onClaimPlace}
          />
        </APIProvider>
      </MapErrorBoundary>
      {authFailed && (
        <div className="places-map__overlay">
          <MapMessage title="Google Maps rechazó la API key">
            Revisá que este dominio ({window.location.host}) esté permitido en la key y que Maps
            JavaScript API esté habilitada.
          </MapMessage>
        </div>
      )}
    </Box>
  );
};

export default PlacesMap;
